import { useMutation, useSuspenseQuery } from '@apollo/client';
import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Form,
  FormField,
  FormItem,
  FormMessage,
  Heading,
  Icon,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  Textarea,
  cn,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useNamedLogger } from '@eluve/logger';

const getArtifactsQuery = graphql(`
  query getArtifacts {
    evalSourceArtifact {
      __typename
      id
      name
      type
      description
      createdAt
    }
  }
`);

const insertDatasetAndArtifactsMutation = graphql(`
  mutation createDataset($dataset: EvalDatasetsInsertInput!) {
    insertEvalDatasetsOne(object: $dataset) {
      __typename
      id
    }
  }
`);

const formSchema = z.object({
  name: z.string().min(3),
  description: z.string(),
  artifacts: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      description: z.string().nullable(),
    }),
  ),
});

type FormData = z.infer<typeof formSchema>;

const defaultValues: Partial<FormData> = {
  name: '',
  description: '',
  artifacts: [],
};

export const CreateDatasetPage: React.FC = () => {
  const toast = useToast();
  const logger = useNamedLogger('create-dataset-page');
  const navigate = useNavigate();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
  });

  const {
    data: { evalSourceArtifact: allArtifacts },
  } = useSuspenseQuery(getArtifactsQuery);

  // Filter out artifacts that are already selected
  const availableArtifacts = allArtifacts.filter(
    (artifact) =>
      !form.getValues('artifacts').some((a) => a.id === artifact.id),
  );

  const [artifactSelectPopoverOpen, setArtifactSelectPopoverOpen] =
    useState(false);
  const [selectedArtifactId, setSelectedArtifactId] = useState('');
  const selectedArtifact = availableArtifacts.find(
    (artifact) => artifact.id === selectedArtifactId,
  );

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'artifacts',
  });

  const [insertDatasetAndArtifacts] = useMutation(
    insertDatasetAndArtifactsMutation,
    {
      onCompleted: (data) => {
        toast.success('Successfully created dataset');
        form.reset(defaultValues);

        if (data.insertEvalDatasetsOne?.id) {
          navigate(
            `/admin/fact-verification/datasets/${data.insertEvalDatasetsOne.id}`,
          );
        }
      },

      onError: () => {
        toast.error('Failed to create dataset');
      },
    },
  );

  const onSubmit = async ({ name, description, artifacts }: FormData) => {
    await insertDatasetAndArtifacts({
      variables: {
        dataset: {
          name,
          description,
          datasetSourceArtifacts: {
            data: artifacts.map((artifact) => ({
              sourceArtifactId: artifact.id,
            })),
          },
        },
      },
    });
  };

  return (
    <div>
      <div className="my-2 py-2">
        <Heading>Create a Dataset</Heading>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (invalid) =>
            logger.debug(
              `Invalid form submission attempted: ${JSON.stringify(invalid)}`,
            ),
          )}
        >
          <Heading size="3xl">Info</Heading>
          <FormField
            control={form.control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <FormItem className="max-w-lg">
                <label htmlFor="name" className="sr-only">
                  Name
                </label>
                <Input className="bg-white/5" placeholder="Name" {...field} />
                {error && (
                  <FormMessage className="mt-4">{error.message}</FormMessage>
                )}
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <FormItem className="max-w-lg">
                <label htmlFor="description" className="sr-only">
                  Description
                </label>
                <Textarea
                  className="bg-white/5"
                  placeholder="Description"
                  {...field}
                />
                {error && (
                  <FormMessage className="mt-4">{error.message}</FormMessage>
                )}
              </FormItem>
            )}
          />

          <Heading size="3xl" className="mt-3">
            Select source artifacts
          </Heading>

          <Heading size="xl" weight="semibold">
            Select source artifacts to add to this dataset
          </Heading>

          <div className="grid gap-2">
            {fields.map((field, index) => (
              <Stack
                className="rounded-md p-3"
                key={field.id}
                direction="horizontal"
                align="start"
                justify="between"
                gap={2}
                fullWidth={false}
              >
                <Stack gap={2} align="stretch">
                  <Heading size="2xl" weight="semibold">
                    {field.name}
                  </Heading>
                  <Text>{field.description}</Text>

                  <div className="h-0.5 rounded-sm"></div>
                </Stack>

                <Button variant="destructive" onClick={() => remove(index)}>
                  <Icon name="Trash" size="xl" />
                </Button>
              </Stack>
            ))}
            <Stack
              className="align-top"
              direction="horizontal"
              gap={3}
              align="stretch"
            >
              <Popover
                open={artifactSelectPopoverOpen}
                onOpenChange={setArtifactSelectPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={artifactSelectPopoverOpen}
                    className="w-[500px] justify-between"
                  >
                    {selectedArtifactId
                      ? selectedArtifact?.name
                      : 'Select artifact...'}
                    <Icon
                      name="ChevronsUpDown"
                      className="ml-2 shrink-0 opacity-50"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[500px] p-0">
                  <Command>
                    <CommandInput placeholder="Search by artifact name..." />
                    <CommandEmpty>No artifacts found.</CommandEmpty>
                    <CommandGroup>
                      {availableArtifacts.map((artifact) => (
                        <CommandItem
                          key={artifact.id}
                          value={artifact.name ?? artifact.id}
                          onSelect={() => {
                            setSelectedArtifactId(
                              artifact.id === selectedArtifactId
                                ? ''
                                : artifact.id,
                            );
                            setArtifactSelectPopoverOpen(false);
                          }}
                        >
                          <Icon
                            name="Check"
                            className={cn(
                              'mr-2',
                              selectedArtifactId === artifact.id
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />

                          <div>
                            <Text className="text-black">{artifact.name}</Text>
                            <Text>{artifact.description}</Text>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
              <Button
                className="w-fit px-4"
                type="button"
                variant="secondary"
                disabled={!selectedArtifact}
                onClick={() => {
                  append({
                    id: selectedArtifactId,
                    name: selectedArtifact!.name ?? null,
                    description: selectedArtifact!.description ?? null,
                  });
                  setSelectedArtifactId('');
                }}
              >
                <Icon name="Plus" size="xl" className="mr-2" />
                Add source artifact
              </Button>
            </Stack>
            <Button className="mt-8 w-fit" disabled={!form.formState.isDirty}>
              Create Dataset
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
